import { useEffect, useMemo, useState } from "react";
import { PrimaryButton } from "../../components/buttons/PrimaryButton";
import Card from "../../components/card/Card";
import { TextInput } from "../../components/inputs/TextInput";
import Header from "../../components/layout/Header";
import { Page, PageBody, PageHeader } from "../../components/layout/Page";
import "./jobs.css";
import CheckBox from "../../components/inputs/CheckBox";
import { formatDate, timestamp } from "../../lib/dateHelper";
import { NavLink, useNavigate, useParams } from "react-router-dom";
import { useFindById, useListRecords } from "../../server/hooks/getData";
import { SecondaryButton } from "../../components/buttons/SecondaryButton";
import { backArrow, calIcon, dockIcon, mailIcon, openInNewIcon, save, trash } from "../../assets/icons";
import Form from "../../components/inputs/Form";
import { pattern } from "../../lib/pattern";
import { Loader } from "../../components/loader/Loader";
import TimeLine from "../../components/layout/timeline";
import {
  createRecord,
  deleteRecord,
  findRecordById,
  listRecords,
  sendEmail,
  updateRecord,
} from "../../server/fetchFunctions";
import { ButtonGroup } from "../../components/buttons/ButtonGroup";
import Inline from "../../components/inline/Inline";
import { HoverButton } from "../../components/buttons/HoverButton";
import { Autocomplete } from "../../components/inputs/Autocomplete";
import { Data } from "../../lib/typeDeclarations";
import { Textarea } from "../../components/inputs/Textarea";
import { format } from "../../lib/inputFormatters";
import { DateInput } from "../../components/inputs/Datepicker";

export default function EditJob() {
  document.title = "Munka szerkesztése";
  const navigate = useNavigate();
  const id = useParams().id || "";

  const { data: initJob, loading: jobL } = useFindById("jobs", [id], id);
  const { data: initExpenses, loading: expensesL } = useListRecords("addExpenses", [], {
    job_id: id,
  });

  const [job, setJob] = useState<{ [key: string]: any }>({});
  const [newExpense, setNewExpense] = useState<{ [key: string]: any }>({});
  const [expenses, setExpenses] = useState(initExpenses.data || []);
  const [loading, setLoading] = useState(true);
  const zipcodes = useListRecords("zipcodes", [job.client_zip], {
    zip: job.client_zip,
    limit: 10,
  });

  //Hooks

  useEffect(() => {
    if (!jobL) {
      setLoading(true);
      setExpenses(initExpenses.data);
      setJob({
        ...initJob,
        date: formatDate(initJob.date),
        promise_date: initJob.promise_date && formatDate(initJob.promise_date),
      });
      setLoading(false);
    }
  }, [jobL, expensesL, initJob, initExpenses]);

  const sum = useMemo(() => {
    let total = 0;
    let exp = 0;
    if (expenses.length !== 0) {
      exp = expenses.reduce((a, b) => a + Number(b.amount), 0);
    }
    job.calibration === 1 ? (total += Number(job.cal_price)) : (total += 0);
    job.qualify === 1 ? (total += Number(job.qualify_price)) : (total += 0);
    total += exp;
    return { net: format.toMoney(total), total: format.toMoney(Math.round(total * 1.27)) };
  }, [job.calibration, , job.qualify, job.cal_price, job.qualify_price, JSON.stringify(expenses)]);

  //Functions
  const openJobPdf = () => {
    window.open(`/jobs/${id}/pdf`, "_blank");
  };

  const sendConfirm = async () => {
    setLoading(true);
    const response = await sendEmail("email", id, "confirm");

    if (response === 200) {
      const statusId = await listRecords("status", { status_name: "Folyamatban" });
      const newInstance = {
        job_id: id,
        note: "Visszaigazoló e-mail kiküldve",
        status_id: statusId.data[0].id,
        status_name: statusId.data[0].status_name,
        status_color: statusId.data[0].color,
        timestamp: timestamp(),
      };
      await createRecord("timeline", newInstance);
      setLoading(false);
    }
  };

  const sendComplete = async () => {
    setLoading(true);
    const response = await sendEmail("email", id, "completed");

    if (response === 200) {
      const statusId = await listRecords("status", { status_name: "Folyamatban" });
      const newInstance = {
        job_id: id,
        note: "Készrejelentés e-mail kiküldve",
        status_id: statusId.data[0].id,
        status_name: statusId.data[0].status_name,
        status_color: statusId.data[0].color,
        timestamp: timestamp(),
      };
      await createRecord("timeline", newInstance);
    }
    setLoading(false);
  };

  const handleNewExpense = async () => {
    const response = await createRecord("addExpenses", { ...newExpense, job_id: id });
    setExpenses((prev: any[]) => [...prev, response]);
    setNewExpense({ description: "", amount: "" });
  };

  const handleExpenseUpdate = async (id: number, value: any) => {
    await updateRecord("addExpenses", id, value);
  };

  const handleExpenseDelete = async (id: number) => {
    await deleteRecord("addExpenses", id);
    setExpenses((prev: any[]) => prev.filter((item) => item.id !== id));
  };

  const handleAutoClompleteZip = (item: Data) => {
    setJob((prev) => ({
      ...prev,
      client_zip: item.zip,
      client_city: item.city,
    }));
  };

  const onSubmit = async (e: any) => {
    e.preventDefault();
    console.log("FFF");
    setLoading(true);
    const calnumber =
      job.calibration === 1 && job.cal_number === null ? await findRecordById("getNextNumber", "calnumber") : null;
    const calnumberPrefix =
      job.calibration === 1 && job.cal_number === null ? await listRecords("counters", { name: "calnumber" }) : null;
    await updateRecord("jobs", id, {
      ...job,
      device_id: job.client_device_id,
      cal_number: job.calibration === 1 && job.cal_number === null ? calnumber.next_number : job.cal_number,
      cal_number_prefix:
        job.calibration === 1 && job.cal_number === null ? calnumberPrefix.data[0].prefix : job.cal_number_prefix,
    });
    setLoading(false);
  };

  return (
    <Page>
      {loading ? (
        <Loader />
      ) : (
        <Form onSubmit={(e) => onSubmit(e)}>
          <Header>
            {job.calibration === 1 && (
              <PrimaryButton
                btn={{
                  svgBefore: calIcon,
                  text: "Kalibrálás",
                  onClick: () => navigate("calibration"),
                }}
              />
            )}
            <ButtonGroup btnArray={[{ svgBefore: dockIcon, text: `Munkalap`, onClick: openJobPdf }]} />
            <ButtonGroup
              btnArray={[
                { svgBefore: mailIcon, text: `Rögzítve`, onClick: sendConfirm },
                { svgBefore: mailIcon, text: "Átvehető", onClick: sendComplete },
              ]}
            />

            <SecondaryButton btn={{ svgBefore: backArrow, text: "Vissza", onClick: () => navigate(-1) }} />
            <PrimaryButton btn={{ svgBefore: save, text: "Ment", type: "submit" }} />
          </Header>
          <PageHeader title="Munka szerkesztése">
            <h3>
              Munkaszám:{" "}
              <span style={{ fontWeight: 600, textDecoration: "underline" }}>{`${job.job_number_prefix ?? ""}${
                job.job_number
              }`}</span>
            </h3>
            <h3>
              Kalibrálási szám:{" "}
              <span style={{ fontWeight: 600, textDecoration: "underline" }}>{`
              ${job.cal_number ? `${job.cal_number_prefix ?? ""}${job.cal_number}` : "---"}
              `}</span>
            </h3>
            <div style={{ marginBottom: 10 }}></div>
            <h3>
              Munkadíj:{" "}
              <span style={{ fontWeight: 600, textDecoration: "underline" }}>
                {sum.net} Ft / {sum.total} Ft (Br.)
              </span>
            </h3>
          </PageHeader>

          <PageBody>
            <div className="jobs">
              <div className="col-1">
                <Card
                  title={
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <span>Ügyfél</span>
                      <NavLink target="_blank" to={`/clients/${initJob.client_id}`}>
                        {openInNewIcon}
                      </NavLink>
                    </div>
                  }>
                  <TextInput
                    stateKey="client_name"
                    inlineLabel="Név"
                    state={job}
                    setState={setJob}
                    pattern={pattern.normal_text}
                    labelWidth={170}
                    required
                  />
                  <TextInput
                    stateKey="client_vat"
                    inlineLabel="Adószám"
                    state={job}
                    setState={setJob}
                    pattern={pattern.vat}
                    labelWidth={170}
                    format="toVatnumber"
                  />
                  <Autocomplete
                    state={job}
                    setState={setJob}
                    stateKey="client_zip"
                    list={zipcodes.data.data}
                    listKeys={["zip", "city"]}
                    handleClick={handleAutoClompleteZip}
                    inlineLabel="Ir. szám"
                    labelWidth={170}
                    pattern={pattern.zip}
                    required
                    format="toInteger"
                  />

                  <TextInput
                    stateKey="client_city"
                    inlineLabel="Település"
                    state={job}
                    setState={setJob}
                    pattern={pattern.normal_text}
                    labelWidth={170}
                    required
                  />
                  <TextInput
                    stateKey="client_street"
                    inlineLabel="Közterület"
                    state={job}
                    setState={setJob}
                    pattern={pattern.normal_text}
                    labelWidth={170}
                    required
                  />
                </Card>
                <Card title="Kapcsolattartó">
                  <TextInput
                    stateKey="contact_name"
                    inlineLabel="Név"
                    state={job}
                    setState={setJob}
                    pattern={pattern.normal_text}
                    labelWidth={170}
                    required
                  />

                  <TextInput
                    stateKey="contact_phone"
                    inlineLabel="Telefon"
                    state={job}
                    setState={setJob}
                    pattern={pattern.phone}
                    format="toPhoneNumber"
                    labelWidth={170}
                    required
                  />
                  <TextInput
                    stateKey="contact_email"
                    inlineLabel="E-mail"
                    state={job}
                    setState={setJob}
                    pattern={pattern.email}
                    type="email"
                    labelWidth={170}
                    required
                  />
                </Card>
                <Card
                  title={
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <span>Eszköz</span>
                      <NavLink target="_blank" to={`/devices/${initJob.device_id}`}>
                        {openInNewIcon}
                      </NavLink>
                    </div>
                  }>
                  <TextInput
                    stateKey="device"
                    inlineLabel="Típus"
                    state={job}
                    setState={setJob}
                    labelWidth={170}
                    readonly
                  />
                  <TextInput
                    stateKey="device_manufacturer"
                    inlineLabel="Gyártó"
                    state={job}
                    setState={setJob}
                    labelWidth={170}
                    readonly
                  />
                  <TextInput
                    stateKey="device_description"
                    inlineLabel="Megnevezés"
                    state={job}
                    setState={setJob}
                    labelWidth={170}
                    readonly
                  />
                  <TextInput
                    stateKey="serial_no"
                    inlineLabel="Gyáriszám"
                    state={job}
                    setState={setJob}
                    labelWidth={170}
                    readonly
                  />
                  <TextInput
                    stateKey="accessories"
                    inlineLabel="Leadott tartozékok"
                    state={job}
                    setState={setJob}
                    labelWidth={170}
                    readonly
                  />
                </Card>
              </div>
              <div className="col-2">
                <TimeLine id={id} setJob={setJob}></TimeLine>
                <Card title="Munka részletei">
                  <TextInput
                    stateKey="date"
                    inlineLabel="Átvétel dátuma"
                    state={job}
                    setState={setJob}
                    labelWidth={170}
                    readonly
                  />
                  <DateInput
                    state={job}
                    setState={setJob}
                    stateKey="promise_date"
                    inlineLabel="Határidő"
                    labelWidth={170}
                  />

                  <Textarea label="Megjegyzés" stateKey="job_note" state={job} setState={setJob} />

                  <CheckBox name="calibration" value={job} setValue={setJob} label="Kalibrálás" />
                  {job.calibration === 1 && (
                    <>
                      <CheckBox name="qualify" value={job} setValue={setJob} label="Minősítés" />
                      <TextInput
                        stateKey="cal_price"
                        inlineLabel="Kalibrálási költség"
                        state={job}
                        setState={setJob}
                        pattern={pattern.number}
                        format="toNumber"
                        afterText="Ft"
                        labelWidth={170}
                        required
                      />
                      {job.qualify === 1 && (
                        <TextInput
                          stateKey="qualify_price"
                          inlineLabel="Minősítési költség"
                          state={job}
                          setState={setJob}
                          pattern={pattern.number}
                          format="toNumber"
                          afterText="Ft"
                          labelWidth={170}
                          required
                        />
                      )}
                    </>
                  )}
                  <CheckBox name="repair" value={job} setValue={setJob} label="Javítás" />
                  {job.repair === 1 && (
                    <>
                      <CheckBox name="warranty" value={job} setValue={setJob} label="Garanciális" />
                      <Textarea
                        label="Hiba leírása"
                        stateKey="fault_description"
                        state={job}
                        setState={setJob}
                        required
                      />
                      <TextInput
                        stateKey="examination_price"
                        inlineLabel="Bevizsg. költség"
                        state={job}
                        setState={setJob}
                        pattern={pattern.number}
                        format="toNumber"
                        afterText="Ft"
                        labelWidth={170}
                        required
                      />
                    </>
                  )}
                </Card>
                <Card title="Egyéb költségek">
                  <Inline style={{ marginBottom: 10 }}>
                    <div style={{ flexGrow: 1 }}>
                      <TextInput
                        stateKey="description"
                        inlineLabel="Megnevezés"
                        state={newExpense}
                        setState={setNewExpense}
                        pattern={pattern.normal_text}
                      />
                    </div>
                    <div style={{ flexGrow: 1 }}>
                      <TextInput
                        stateKey="amount"
                        inlineLabel="Összeg"
                        state={newExpense}
                        setState={setNewExpense}
                        pattern={pattern.normal_text}
                        afterText="Ft"
                        format="toNumber"
                      />
                    </div>
                    <div style={{ flexGrow: 0, display: "flex" }}>
                      <HoverButton onClick={handleNewExpense}>{save}</HoverButton>
                    </div>
                  </Inline>
                  <ul style={{ marginTop: 10 }}>
                    {!expensesL &&
                      expenses.map((item, index) => (
                        <li key={index}>
                          <Inline style={{ marginBottom: 10 }}>
                            <div style={{ flexGrow: 1 }}>
                              <TextInput
                                stateKey="description"
                                inlineLabel="Megnevezés"
                                state={expenses[index]}
                                setState={setExpenses}
                                itemOfArr={item}
                                pattern={pattern.normal_text}
                              />
                            </div>
                            <div style={{ flexGrow: 1 }}>
                              <TextInput
                                stateKey="amount"
                                inlineLabel="Összeg"
                                state={expenses[index]}
                                setState={setExpenses}
                                itemOfArr={item}
                                pattern={pattern.number}
                                afterText="Ft"
                                format="toNumber"
                              />
                            </div>
                            <div style={{ flexGrow: 0, display: "flex" }}>
                              <HoverButton
                                onClick={() =>
                                  JSON.stringify(initExpenses.data[index]) === JSON.stringify(expenses[index])
                                    ? handleExpenseDelete(item.id)
                                    : handleExpenseUpdate(item.id, expenses[index])
                                }>
                                {JSON.stringify(initExpenses.data[index]) === JSON.stringify(expenses[index])
                                  ? trash
                                  : save}
                              </HoverButton>
                            </div>
                          </Inline>
                        </li>
                      ))}
                  </ul>
                </Card>
              </div>
            </div>
          </PageBody>
        </Form>
      )}
    </Page>
  );
}
